import Api from '@/models/Api'
import SystemDashboardWidget from '@/models/SystemDashboardWidget'
import { buildColorPool } from '@/models/Util'
import numeral from 'numeral'
import SalesActivity from '@/models/SalesActivity'
import { FILTERS } from '../../helpers'
import RegionSalesRepGroup from '../../filters/RegionSalesRepGroup'
import PeriodGroup from '../../filters/PeriodGroup'
import DateRangeFilter from '../../filters/DateRangeFilter'

export default class TargetOpportuntyWidget extends SystemDashboardWidget {
  public title: string = 'Target Opportunities'

  public key: string = 'target-ops'

  public view: string = 'chart'

  public subView: string = 'line'

  public dimensions: string[] = ['date', 'activity']

  public metrics: string[] = ['amount']

  public loaderSize: string = '350px'

  public size: any = {
    sm: 12,
    md: 8,
    lg: 8,
  }

  public dataSourceRequest: any = null

  public format_totals: any = '0,0'

  constructor(args: any = null) {
    super()

    if (args && args.dimensions) {
      this.dimensions = args.dimensions
    }

    if (args && args.metrics) {
      this.metrics = args.metrics
    }

    if (args && args.filters) {
      this.filters = Object.assign(this.filters, args.filters)
    }
    if (args && args.internalFilters) {
      this.internalFilters = Object.assign(this.internalFilters, args.internalFilters)
    }
    if (args && args.internalQuery) {
      this.internalQuery = Object.assign(this.internalQuery, args.internalQuery)
    }

    if (args && args.rightCol) {
      this.rightCol = args.rightCol
    }

    if (args && args.leftCol) {
      this.leftCol = args.leftCol
    }

    if (args && args.size) {
      this.size = args.size
    }

    if (args && args.title) {
      this.title = args.title
    }

    if (args && args.key) {
      this.key = args.key
    }

    if (args && args.dataSourceRequest) {
      this.dataSourceRequest = args.dataSourceRequest
    }

    // format_totals
    if (args && args.format_totals) {
      this.format_totals = args.format_totals
    }
  }

  // API Filters
  public filters: any = {
    region: 'all',
    product: 'all',
  }

  public groups = {
    view: 'region',
    period: 'week',
  }

  public internalFilters: any = {
    ...FILTERS.LAST_4_WEEKS,
  }

  public leftCol: any = [new RegionSalesRepGroup(), new PeriodGroup()]

  public rightCol: any = [new DateRangeFilter({ target: 'internalFilters' })]

  public get filterToQuery() {
    let query = []

    if (this.filters.product !== 'all') {
      query.push(`product:${this.filters.product}`)
    }

    if (this.filters.region !== 'all') {
      query.push(`region:${this.filters.region}`)
    }

    return query
  }

  public get groupList() {
    let group_mode = this.groups.period
    let group_role = this.groups.view === 'region' ? 'team' : 'individual'
    return {
      group_mode,
      group_role,
    }
  }

  public get payload() {
    const query = [...new Set([...this.query, ...this.internalQuery, ...this.filterToQuery])]

    return {
      start: this.filters.start ?? this.internalFilters.start,
      end: this.filters.end ?? this.internalFilters.end,
      query,
      ...this.groupList,
    }
  }

  public formatters: any = {
    yaxis: (_: SystemDashboardWidget, value: any) => `${numeral(value).format(this.format_totals)}`,
    label: (_: SystemDashboardWidget, value: any) => `${numeral(value).format(this.format_totals)}`,
    tooltip: (_: SystemDashboardWidget, value: any) => [numeral(value).format(this.format_totals)],
  }

  public dataSource() {
    if (this.dataSourceRequest) {
      const res = this.dataSourceRequest(this.payload)

      return res
    }
    let request = new SalesActivity()

    return request
      .targetOps({ module: this.payload, ignore_logic: true })
      .then((res: any) => res.result)
  }

  protected dataMapper(data: any): Promise<any> {
    const series: number[] = []
    const data_series: any[] = []
    const labels: any[] = []
    const colors: string[] = []
    const series_ids: string[] = []
    let total = 0
    let total_cumulative: any[] = []

    const groups: string[] = []

    let x_series: string[] = []

    // Init Series
    data.forEach((item: any, idx: number) => {
      const label = item[this.dimensions[1]]
      const group_index = groups.indexOf(label)
      if (group_index === -1) {
        groups.push(label)
        data_series.push({ name: label, data: [] })
      }

      const x_label = item[this.dimensions[0]]

      if (x_series.indexOf(x_label) === -1) {
        x_series.push(x_label)
      }
    })

    const color_pool = buildColorPool(groups.length)

    data_series.forEach((item: any, idx: number) => {
      x_series.forEach(() => {
        item.data.push(0)
      })
      if (item.name === 'CCL') {
        colors.push('#555c6e')
      } else if (item.name === 'SSL') {
        colors.push('#00C1DE')
      } else {
        colors.push(color_pool(idx))
      }
    })

    data.forEach((item: any, idx: number) => {
      const label = item[this.dimensions[0]]
      const group_label = item[this.dimensions[1]]
      const group_index = groups.indexOf(group_label)
      const x_index = x_series.indexOf(label)

      let val = Number(item[this.metrics[0]])
      if (isNaN(val)) {
        val = 0
      }
      if (!total_cumulative[group_index]) {
        total_cumulative[group_index] = 0
      }

      total += val
      total_cumulative[group_index] += val

      series.push(val)

      data_series[group_index].data[x_index] += val

      if (['product', 'region', 'deal_type'].includes(this.dimensions[1])) {
        series_ids[group_index] = item[this.dimensions[1]].toLowerCase().replaceAll(' ', '_')
      } else if (this.dimensions[2]) {
        series_ids[group_index] = item[this.dimensions[2]]
      }
    })

    // Udpate Settings
    this.chartSettings.colors = colors
    this.chartSettings.xaxis = {
      categories: x_series,
    }
    this.chartSettings.labels = labels

    return Promise.resolve(data_series)
  }
}
